import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { Filter, ShieldAlert, Clock } from "lucide-react";
import ReworkIcon from "./icons/ReworkIcon";
import { formatCurrency } from "@/components/dashboard/utils";
import { isItemBlocked, isItemOverdue, startOfToday } from "@/components/StagePage/stageFlags";

/* ── Pipeline rail (header redesign "Layout 1", 2-Nov-2026) ─────────────────
   Row 2 of the ERP header, and the only thing on it: Dashboard is the rail's
   first cell (the "Stacked" tile — icon over the word), then one tile per
   visible stage. Spec: plans/pipeline-header-redesign.md + the mock beside it.

   · Track (.rl-rail-glass, 3-Nov-2026) — a solid card fill wearing the
     dashboard hero cards' rim + shadow; no transparency, no blur. Never
     pressed in; a track is not a state. The drop shadow lives on
     .rl-rail-shell because the rail's own clip-path would cut it off.
   · Every stage tile always wears a faint chevron in its stage colour, and the
     chevrons interlock (16px point/notch overhang, 4px visible gap), so the bar
     reads as a chain of arrows. No step numbers, no › separators.
   · The thumb is a STRETCHY ARROW: its left/right edges are two registered
     custom properties (--l / --r), each with its own transition — the leading
     edge springs ahead, the trailing edge follows. Dashboard / first / last
     stage use a no-overshoot ease so the arrow never springs past the rail's
     rounded ends (the rail's clip-path masks whatever is left).
   · Hero mode on "/": tiles grow and add value (can_view_value only) + a 7-day
     sparkline; they collapse back on any other route.

   Timing: StagePage holds its heavy row commit for HOLD_MS (520) so this
   stretch runs on a free main thread. Keep lead + tail delay + tail ≤ ~0.5s,
   or retune HOLD_MS with it.

   Separate class names (.rl-flow-*) on purpose: Ecotile and Maintenance still
   use .pipeline-nav / .pipe-thumb and must not change. */

// Chevron point overhang (= App.css --ov): it scales with tile height so the
// tip angle stays ~110°. Only the POINT overhangs (the notch sits inside the
// tile), so the thumb spans [tile left, tile right + point].
const OV_COMPACT = 20;                 // 56px tiles
const OV_HERO = 28;                    // 82px tiles on "/"
const LEAD_EASE = "0.3s cubic-bezier(0.3, 1.3, 0.5, 1)";       // springs
const LEAD_EDGE = "0.3s cubic-bezier(0.22, 0.7, 0.3, 1)";      // no overshoot
const TAIL      = "0.44s cubic-bezier(0.55, 0, 0.2, 1) 0.04s"; // follows

// Rainbow mesh the thumb wears on the Dashboard (today's dashboard button).
const HOME_COLOR = "#fbbf24";

function Sparkline({ data, color }) {
  if (!data || data.length < 2) return null;
  const w = 58, h = 16;
  const min = Math.min(...data), max = Math.max(...data);
  const span = max - min || 1;
  const pts = data.map((v, i) =>
    `${((i / (data.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / span) * (h - 4)).toFixed(1)}`
  ).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" className="rl-flow-spark">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function PipelineRail({
  stages,                // visibleNav: Dashboard ("/") + role-filtered stages
  pathname,
  onNavigate,
  lensItems,             // items already scoped by the division lens
  getStageCount,
  getFilteredStageCount,
  reworkCount,
  lastAdvanceInfo,
  showValue,
  trend,
  reduced,               // Performance Mode or prefers-reduced-motion → snap
}) {
  const railRef = useRef(null);
  const thumbRef = useRef(null);
  const cellRefs = useRef(new Map());
  const prevIdxRef = useRef(null);     // index of the cell the thumb last sat on

  const { stageCells, home, cells } = useMemo(() => {
    const st = stages.filter(s => s.path !== "/");
    const h = stages.find(s => s.path === "/");
    return { stageCells: st, home: h, cells: h ? [h, ...st] : st };
  }, [stages]);
  const activeIdx = cells.findIndex(s => s.path === pathname);
  const hero = pathname === "/";

  // Per-stage blocked / overdue / value, lens-scoped like the counts beside them.
  const perStage = useMemo(() => {
    const today = startOfToday();
    const out = {};
    for (const i of lensItems) {
      const s = out[i.stage] || (out[i.stage] = { blocked: 0, overdue: 0, value: 0 });
      if (isItemBlocked(i)) s.blocked += 1;
      if (isItemOverdue(i, today)) s.overdue += 1;
      s.value += i.value_usd || 0;
    }
    return out;
  }, [lensItems]);

  const place = useCallback((instant) => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    const target = activeIdx >= 0 ? cellRefs.current.get(cells[activeIdx].path) : null;
    if (!target) {
      thumb.style.opacity = "0";
      prevIdxRef.current = null;
      return;
    }
    const isHome = cells[activeIdx].path === "/";
    const firstStage = home ? 1 : 0;
    const isFirst = activeIdx === firstStage;
    const isLast = activeIdx === cells.length - 1;
    thumb.classList.toggle("home", isHome);
    thumb.classList.toggle("first", isFirst && !isHome);
    thumb.classList.toggle("last", isLast && !isHome);
    // Use the point size the rail is HEADING to (--ov glides with the hero
    // grow/shrink, so reading it mid-transition would give a stale value).
    const ov = pathname === "/" ? OV_HERO : OV_COMPACT;
    const ro = isHome || isLast ? 0 : ov;
    const L = target.offsetLeft;
    const R = target.offsetLeft + target.offsetWidth + ro;
    const set = () => {
      thumb.style.setProperty("--l", `${L}px`);
      thumb.style.setProperty("--r", `${R}px`);
      thumb.style.backgroundColor = isHome ? HOME_COLOR : (cells[activeIdx].color || HOME_COLOR);
    };
    const from = prevIdxRef.current;
    prevIdxRef.current = activeIdx;
    if (instant || reduced || from === null || thumb.style.opacity !== "1") {
      thumb.style.transition = "none";
      set();
      void thumb.offsetHeight;
      thumb.style.transition = "";
      thumb.style.opacity = "1";
      return;
    }
    // Stretchy arrow: the leading edge leaps, the trailing edge follows.
    const fwd = activeIdx > from;
    const lead = isHome || isFirst || isLast ? LEAD_EDGE : LEAD_EASE;
    thumb.style.transition =
      `--l ${fwd ? TAIL : lead}, --r ${fwd ? lead : TAIL}, background-color 0.3s ease, opacity 0.25s ease`;
    set();
    thumb.style.opacity = "1";
  }, [activeIdx, cells, home, reduced, pathname]);

  // Mount snaps; route changes glide. Layout effect = before paint, so no
  // background-tab rAF freeze. A rAF + fonts.ready top-up re-snaps on mount
  // once late fonts settle the tile widths.
  const mountedRef = useRef(false);
  useLayoutEffect(() => {
    const first = !mountedRef.current;
    mountedRef.current = true;
    place(first);
    if (!first) return undefined;
    const raf = requestAnimationFrame(() => place(true));
    let live = true;
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => { if (live) place(true); });
    return () => { live = false; cancelAnimationFrame(raf); };
  }, [pathname, stages.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tile widths only change when the rail itself gets WIDER or NARROWER
  // (window, zoom). SKIP the observer's initial callback: it fires on observe()
  // and would land a frame into a glide and SNAP it (the division-rail lesson).
  // Ignore HEIGHT-only changes too: the hero grow/shrink on entering/leaving
  // "/" resizes the rail every frame, and re-placing then snapped the arrow
  // instead of letting it slide to or from Dashboard (bug, 2-Nov-2026).
  const placeRef = useRef(place);
  placeRef.current = place;
  useEffect(() => {
    const rail = railRef.current;
    if (!rail || typeof ResizeObserver === "undefined") return undefined;
    let primed = false;
    let lastWidth = rail.offsetWidth;
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width ?? rail.offsetWidth);
      if (!primed) { primed = true; lastWidth = w; return; }
      if (w === lastWidth) return;
      lastWidth = w;
      placeRef.current(true);
    });
    ro.observe(rail);
    return () => ro.disconnect();
  }, []);

  const firstStagePath = stageCells[0]?.path;
  const lastStagePath = stageCells[stageCells.length - 1]?.path;

  return (
    <div className="rl-rail-shell">
    <div
      ref={railRef}
      className={`rl-flow-rail rl-rail-glass${hero ? " hero" : ""}`}
      role="navigation"
      aria-label="Pipeline"
      data-testid="pipeline-rail"
    >
      <div ref={thumbRef} className="rl-flow-thumb" aria-hidden="true"><i className="rl-flow-mesh" /></div>
      {cells.map((s) => {
        const isActive = s.path === pathname;
        const setRef = (el) => { if (el) cellRefs.current.set(s.path, el); else cellRefs.current.delete(s.path); };
        const testid = `nav-${s.path.replace("/", "") || "dashboard"}`;
        const Icon = s.icon;

        if (s.path === "/") {
          return (
            <button
              key={s.path}
              ref={setRef}
              type="button"
              onClick={() => onNavigate("/")}
              className={`rl-flow-cell rl-flow-home${isActive ? " active" : ""}`}
              aria-current={isActive ? "page" : undefined}
              data-testid={testid}
            >
              <span className="rl-flow-stack">
                <Icon className="rl-flow-home-ico" strokeWidth={1.9} />
                <span>{s.label}</span>
              </span>
            </button>
          );
        }

        const count = getStageCount(s.stage);
        const filterCount = getFilteredStageCount(s.stage);
        const showAdvance = lastAdvanceInfo && lastAdvanceInfo.toStage === s.stage;
        const archive = s.stage === "Completed";
        const st = perStage[s.stage] || { blocked: 0, overdue: 0, value: 0 };
        const cls = [
          "rl-flow-cell",
          s.path === firstStagePath && "first",
          s.path === lastStagePath && "last",
          archive && "archive",
          isActive && "active",
        ].filter(Boolean).join(" ");
        return (
          <button
            key={s.path}
            ref={setRef}
            type="button"
            onClick={() => onNavigate(s.path)}
            className={cls}
            style={{ "--stage": s.color }}
            aria-current={isActive ? "page" : undefined}
            data-testid={testid}
          >
            <span className="rl-flow-c1">
              <Icon className="w-[15px] h-[15px] shrink-0" />
              <span className="truncate">{s.label}</span>
            </span>
            <span className="rl-flow-c2">
              {/* Plain count — REPLACED by the filter-match chip while a global
                  (or due) filter is narrowing this stage: one token, not two. */}
              {count !== null && filterCount === null && (
                <span
                  key={showAdvance ? `pulse-${lastAdvanceInfo.time}` : "static"}
                  className={`rl-flow-num${showAdvance ? " tab-count-advanced" : ""}`}
                >
                  {count}
                </span>
              )}
              {filterCount !== null && (
                <span
                  className="pipe-filter-chip rl-flow-filter"
                  style={isActive
                    ? { background: "rgba(255,255,255,0.24)", color: "#fff" }
                    : filterCount > 0
                      ? { background: `${s.color}26`, color: s.color }
                      : { background: "hsl(var(--muted-foreground) / 0.12)", color: "hsl(var(--muted-foreground))" }}
                  title={`${filterCount} matching ${filterCount === 1 ? "item" : "items"} in ${s.label}`}
                  data-testid={`nav-filter-tag-${s.stage.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <Filter className="w-3 h-3" strokeWidth={2.6} />
                  {filterCount}
                </span>
              )}
              {!archive && st.blocked > 0 && (
                <span className="rl-flow-chip b" title={`${st.blocked} blocked`} data-testid={`nav-blocked-${s.path.replace("/", "")}`}>
                  <ShieldAlert className="w-[11px] h-[11px]" strokeWidth={2.4} />{st.blocked}
                </span>
              )}
              {!archive && st.overdue > 0 && (
                <span className="rl-flow-chip o" title={`${st.overdue} overdue`} data-testid={`nav-overdue-${s.path.replace("/", "")}`}>
                  <Clock className="w-[11px] h-[11px]" strokeWidth={2.4} />{st.overdue}
                </span>
              )}
              {s.stage === "Manufacturing" && reworkCount > 0 && (
                <span
                  className="rl-flow-chip rw"
                  title={`${reworkCount} rework item${reworkCount === 1 ? "" : "s"}`}
                  data-testid="nav-rework-badge"
                >
                  <ReworkIcon className="w-[11px] h-[11px]" strokeWidth={2.4} />{reworkCount}
                </span>
              )}
            </span>
            <span className="rl-flow-c3" aria-hidden={!hero}>
              {archive ? (
                <span>Shipped and closed</span>
              ) : (
                <>
                  {showValue && <span className="tabular-nums">{formatCurrency(st.value)}</span>}
                  <Sparkline data={trend?.series?.[s.stage]} color={isActive ? "#fff" : s.color} />
                </>
              )}
            </span>
          </button>
        );
      })}
    </div>
    </div>
  );
}
